import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    num: '01',
    label: 'THE GENESIS',
    title: 'Audit & $30M Hub Integration',
    desc: 'We analyze your business from end to end and integrate you into the Barkfy ecosystem — with immediate access to a $30M commercial infrastructure.',
    icon: 'ri-search-eye-line',
    accent: '#DC5014',
  },
  {
    num: '02',
    label: 'THE MAKEOVER',
    title: 'Full Genesis Blue & Origin Clay Rebranding',
    desc: 'Your visual identity is rebuilt from scratch with the exclusive Barkfy palette. Logo, website, materials, and authority positioning.',
    icon: 'ri-palette-line',
    accent: '#143C3C',
  },
  {
    num: '03',
    label: 'THE ARENA',
    title: '4,000 SQF Showroom Access & Bank Financing',
    desc: 'Access to our physical 4,000 SQF Showroom in Downtown Orlando — the most powerful closing environment in the market. Plus bank financing for your clients.',
    icon: 'ri-building-2-line',
    accent: '#DC5014',
  },
  {
    num: '04',
    label: 'SCALE',
    title: 'Commercial Launch with the Barkfy Elite Seal',
    desc: 'You enter the market with the Barkfy Elite authority seal — branding, showroom, financing, and a commercial team working for you.',
    icon: 'ri-rocket-line',
    accent: '#143C3C',
  },
];

export default function ClientMeetingSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="meeting"
      ref={ref}
      className="px-5 sm:px-8 md:px-[60px]"
      style={{ background: '#143C3C', paddingTop: '80px', paddingBottom: '80px' }}
    >
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div
          className="text-center mb-14 md:mb-20"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div style={{ width: '32px', height: '1px', background: '#DC5014' }} />
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '11px',
                color: '#DC5014',
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
              }}
            >
              THE PROCESS
            </span>
            <div style={{ width: '32px', height: '1px', background: '#DC5014' }} />
          </div>

          <h2
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(28px, 6vw, 52px)',
              fontWeight: 700,
              color: '#FFFFFF',
              lineHeight: 1.15,
              marginBottom: '16px',
            }}
          >
            From Zero to Corporation.
            <br />
            <span style={{ color: '#DC5014', fontStyle: 'italic' }}>In 4 Steps.</span>
          </h2>

          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 300,
              fontSize: '16px',
              color: 'rgba(255,255,255,0.55)',
              maxWidth: '480px',
              margin: '0 auto',
              lineHeight: 1.8,
            }}
          >
            A structured process to transform elite contractors into authority corporations.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line — desktop only */}
          <div
            className="absolute hidden md:block"
            style={{
              left: '28px',
              top: '0',
              bottom: '0',
              width: '1px',
              background: 'linear-gradient(to bottom, #DC5014, rgba(220,80,20,0.1))',
            }}
          />

          <div className="flex flex-col gap-0">
            {steps.map((step, i) => (
              <div
                key={step.num}
                className="relative"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(32px)',
                  transition: `opacity 0.7s ease ${i * 150}ms, transform 0.7s ease ${i * 150}ms`,
                  paddingBottom: i < steps.length - 1 ? '48px' : '0',
                }}
              >
                {/* Mobile layout: centered icon + content below */}
                <div className="flex flex-col items-center md:hidden text-center mb-4">
                  <div
                    className="flex items-center justify-center rounded-full flex-shrink-0 mb-4"
                    style={{
                      width: '60px',
                      height: '60px',
                      background: step.accent,
                      position: 'relative',
                      zIndex: 2,
                    }}
                  >
                    <i className={step.icon} style={{ color: '#FFFFFF', fontSize: '24px' }} />
                  </div>
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <span
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '11px',
                        color: '#DC5014',
                        letterSpacing: '0.3em',
                        textTransform: 'uppercase',
                      }}
                    >
                      STEP {step.num}
                    </span>
                    <div style={{ width: '16px', height: '1px', background: '#DC5014', opacity: 0.4 }} />
                    <span
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '11px',
                        color: '#DC5014',
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {step.label}
                    </span>
                  </div>
                  <h3
                    style={{
                      fontFamily: '"Playfair Display", serif',
                      fontSize: '20px',
                      fontWeight: 700,
                      color: '#FFFFFF',
                      lineHeight: 1.3,
                      marginBottom: '10px',
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 300,
                      fontSize: '16px',
                      color: 'rgba(255,255,255,0.65)',
                      lineHeight: 1.75,
                      maxWidth: '480px',
                    }}
                  >
                    {step.desc}
                  </p>
                </div>

                {/* Desktop layout: icon left + content right */}
                <div className="hidden md:flex gap-12">
                  <div className="flex-shrink-0 flex items-start" style={{ width: '56px' }}>
                    <div
                      className="flex items-center justify-center rounded-full flex-shrink-0"
                      style={{
                        width: '56px',
                        height: '56px',
                        background: step.accent,
                        position: 'relative',
                        zIndex: 2,
                      }}
                    >
                      <i className={step.icon} style={{ color: '#FFFFFF', fontSize: '22px' }} />
                    </div>
                  </div>
                  <div className="flex-1 pt-2 pb-2">
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        style={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '10px',
                          color: '#DC5014',
                          letterSpacing: '0.3em',
                          textTransform: 'uppercase',
                        }}
                      >
                        STEP {step.num}
                      </span>
                      <div style={{ width: '24px', height: '1px', background: '#DC5014', opacity: 0.4 }} />
                      <span
                        style={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '10px',
                          color: '#DC5014',
                          letterSpacing: '0.2em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {step.label}
                      </span>
                    </div>
                    <h3
                      style={{
                        fontFamily: '"Playfair Display", serif',
                        fontSize: '22px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        lineHeight: 1.3,
                        marginBottom: '12px',
                      }}
                    >
                      {step.title}
                    </h3>
                    <p
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 300,
                        fontSize: '16px',
                        color: 'rgba(255,255,255,0.65)',
                        lineHeight: 1.8,
                        maxWidth: '560px',
                      }}
                    >
                      {step.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div
          className="text-center mt-16 md:mt-20"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease 700ms, transform 0.7s ease 700ms',
          }}
        >
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="font-sans font-bold whitespace-nowrap cursor-pointer transition-all duration-300 hover:brightness-110 active:scale-95"
            style={{
              background: '#DC5014',
              color: '#FFFFFF',
              padding: '0 48px',
              height: '52px',
              minHeight: '52px',
              borderRadius: '4px',
              fontSize: '13px',
              letterSpacing: '0.12em',
              border: 'none',
            }}
          >
            APPLY TO JOIN THE ELITE HUB
          </button>
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontStyle: 'italic',
              fontSize: '11px',
              color: 'rgba(255,255,255,0.35)',
              marginTop: '14px',
              letterSpacing: '0.04em',
            }}
          >
            Limited availability: Only 3 spots left this quarter.
          </p>
        </div>

      </div>
    </section>
  );
}
